import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Quote,
  Play,
  Phone,
  Handshake,
  Megaphone,
  Star,
  PhoneCall,
  CalendarClock,
  Clapperboard,
  Video,
  Users,
  ClipboardList,
  MessageSquare,
  Wrench,
  Route,
} from "lucide-react";
import { Hero } from "@/components/Hero";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { DemoModal } from "@/components/DemoModal";
import { ProblemVisual } from "@/components/ProblemVisual";
import { logDemoOpen, getJSON } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { SITE_URL } from "@/lib/seo";

const Label = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ember)]">
    {children}
  </span>
);

const AGENT_ICONS = {
  handshake: Handshake,
  megaphone: Megaphone,
  star: Star,
  phone: PhoneCall,
  calendar: CalendarClock,
  clapperboard: Clapperboard,
  video: Video,
  users: Users,
  clipboard: ClipboardList,
  message: MessageSquare,
  wrench: Wrench,
  route: Route,
};

// Avatars are derived on the frontend (from the agent's icon) so they deploy
// with the build and stay consistent across environments — independent of the
// avatar value stored per-environment in the database.
const AGENT_PHOTOS = [
  "/agents/korean-male.jpg",
  "/agents/curly-male.jpg",
  "/agents/bald-male.jpg",
  "/agents/pakistani-female.jpg",
  "/agents/black-female.jpg",
  "/agents/pakistani-male.jpg",
];
const AGENT_PHOTO_BY_ICON = {
  handshake: "/agents/korean-male.jpg",
  megaphone: "/agents/curly-male.jpg",
  star: "/agents/bald-male.jpg",
  phone: "/agents/pakistani-female.jpg",
  calendar: "/agents/black-female.jpg",
  clapperboard: "/agents/pakistani-male.jpg",
  video: "/agents/curly-male.jpg",
  users: "/agents/korean-male.jpg",
  clipboard: "/agents/bald-male.jpg",
  message: "/agents/pakistani-female.jpg",
  wrench: "/agents/pakistani-male.jpg",
  route: "/agents/black-female.jpg",
};
const avatarFor = (agent, i) =>
  `${process.env.PUBLIC_URL}${AGENT_PHOTO_BY_ICON[agent.icon] || AGENT_PHOTOS[i % AGENT_PHOTOS.length]}`;

export default function IndustryPage({ industry }) {
  const [modalAgent, setModalAgent] = useState(null);
  const [agents, setAgents] = useState([]);
  const triggerRef = useRef(null);
  const industryKey = industry.route.slice(1);

  useEffect(() => {
    let active = true;
    getJSON(`/industries/${industryKey}/agents`)
      .then((data) => active && setAgents(data))
      .catch(() => active && setAgents([]));
    return () => {
      active = false;
    };
  }, [industryKey]);

  const openDemo = (agent, e) => {
    triggerRef.current = e.currentTarget;
    setModalAgent(agent);
    logDemoOpen({
      agent: agent.name,
      agentKey: agent.key,
      industry: industry.name,
      mode: agent.cta === "live" ? "phone" : "video",
    });
  };

  return (
    <>
      <Seo
        title={`AI for ${industry.name} — Agents That Book, Sell & Follow Up`}
        description={(industry.problem || industry.complianceTag || "").slice(0, 160)}
        path={industry.route}
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: `AI department for ${industry.name}`,
          provider: { "@type": "Organization", name: "The AI Dept.", url: SITE_URL },
          areaServed: "US",
          name: industry.h1,
          description: (industry.problem || "").slice(0, 250),
          url: `${SITE_URL}${industry.route}`,
        }}
      />
      <Hero
        image={industry.heroImage}
        eyebrow={industry.eyebrow}
        title={industry.h1}
        subline={industry.complianceTag}
        primary={{ label: "Install your department", to: "/audit" }}
        secondary={{ label: "See pricing", to: "/pricing" }}
      />

      {/* Industry problem */}
      <section className="bg-[var(--bg-base)] py-28 md:py-36" data-testid="industry-problem">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <Reveal><Label>The problem in {industry.name.toLowerCase()}</Label></Reveal>
          <Reveal delay={80}>
            <p className="mt-6 text-2xl font-semibold leading-snug tracking-[-0.02em] text-[var(--ink)] sm:text-3xl">
              {industry.problem}
            </p>
          </Reveal>
        </div>
      </section>

      {/* AI that solves real problems */}
      <section className="bg-[var(--bg-base)] py-24 md:py-32" data-testid="industry-problem-solvers">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal>
              <h2 className="t-section text-[var(--ink)]">
                AI that solves <span className="text-[var(--ember)]">real problems</span>
              </h2>
            </Reveal>
            <Reveal delay={100}>
              <p className="mt-4 text-base leading-relaxed text-[var(--muted)] sm:text-lg">
                The everyday bottlenecks that quietly cost {industry.name.toLowerCase()} businesses
                revenue — and the agents we install to close each gap.
              </p>
            </Reveal>
          </div>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {(industry.problemSolvers || []).map((item, i) => {
              const Icon = AGENT_ICONS[item.icon];
              return (
                <Reveal key={item.title} delay={(i % 3) * 80}>
                  <div className="group flex h-full flex-col rounded-2xl border border-black/5 bg-[var(--bg-contrast)] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--ember)]/30 hover:shadow-[0_24px_50px_-28px_rgba(20,18,16,0.4)]">
                    <span className="inline-flex w-fit items-center rounded-full border border-[var(--ember)]/25 bg-[var(--ember)]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--ember)]">
                      {item.problem}
                    </span>
                    <div className="mt-6 flex items-start gap-4">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--ember)]/10 text-[var(--ember)]">
                        {Icon && <Icon size={20} strokeWidth={1.8} />}
                      </span>
                      <h3 className="pt-1 text-lg font-semibold leading-snug tracking-tight text-[var(--ink)]">
                        {item.title}
                      </h3>
                    </div>
                    <p className="mt-5 flex-1 text-[15px] leading-relaxed text-[var(--muted)]">
                      {item.body}
                    </p>
                    <ProblemVisual type={item.icon} />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Installed roster */}
      <section className="bg-[var(--bg-contrast)] py-28 md:py-36" data-testid="industry-roster">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-2xl">
            <Reveal><Label>The installed roster</Label></Reveal>
            <Reveal delay={80}>
              <h2 className="mt-7 t-section">
                Meet Your 24/7 AI Team for {industry.name}.
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-4 text-base leading-relaxed text-[var(--muted)] sm:text-lg">
                Each one is a business outcome, run by the team behind it. You
                don't manage agents — you read the results.
              </p>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {agents.map((agent, i) => {
              const Icon = AGENT_ICONS[agent.icon];
              const isLive = agent.cta === "live";
              return (
                <Reveal key={agent.id} delay={(i % 3) * 80}>
                  <div className="group flex h-full flex-col rounded-2xl border border-black/5 bg-[var(--bg-base)] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--ember)]/30 hover:shadow-[0_24px_50px_-28px_rgba(20,18,16,0.4)]">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center rounded-full bg-[var(--ember)]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--ember)]">
                        {agent.badge}
                      </span>
                      {Icon && (
                        <Icon size={22} className="text-[var(--ember)]" strokeWidth={1.7} />
                      )}
                    </div>

                    <div className="mt-6 flex items-center gap-4">
                      <img
                        src={avatarFor(agent, i)}
                        alt={agent.name}
                        className="h-14 w-14 flex-shrink-0 rounded-full object-cover"
                        loading="lazy"
                      />
                      <h4 className="text-lg font-semibold tracking-tight text-[var(--ink)]">
                        {agent.name}
                      </h4>
                    </div>

                    <p className="mt-5 flex-1 text-[15px] leading-relaxed text-[var(--muted)]">
                      {agent.description}
                    </p>

                    <div className="mt-5 flex flex-wrap gap-2">
                      {agent.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full border border-black/10 bg-[var(--bg-contrast)] px-3 py-1 text-[12px] font-medium text-[var(--ink)]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="mt-6 flex justify-end">
                      {isLive ? (
                        <button
                          onClick={(e) =>
                            openDemo(
                              {
                                ...agent,
                                phoneNumber: agent.phone,
                              },
                              e,
                            )
                          }
                          className="inline-flex items-center gap-2 rounded-full bg-[var(--mint)] px-4 py-2 text-[13px] font-semibold text-[var(--charcoal)] transition-all duration-300 hover:bg-[var(--ember-deep)] hover:-translate-y-0.5"
                          data-testid={`demo-cta-${agent.key}`}
                        >
                          <Phone size={14} /> LIVE DEMO
                        </button>
                      ) : (
                        <button
                          onClick={(e) => openDemo(agent, e)}
                          className="inline-flex items-center gap-2 rounded-full border border-[var(--ember)] px-4 py-2 text-[13px] font-semibold text-[var(--ember)] transition-all duration-300 hover:bg-[var(--mint)] hover:text-[var(--charcoal)] hover:-translate-y-0.5"
                          data-testid={`demo-cta-${agent.key}`}
                        >
                          <Play size={13} /> Watch Demo
                        </button>
                      )}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Teams behind your roster — cross-link */}
      <section className="bg-[var(--bg-base)] py-24 md:py-28" data-testid="industry-teams-crosslink">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal><Label>The teams behind your roster</Label></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 max-w-2xl text-2xl font-semibold leading-tight tracking-[-0.02em] sm:text-3xl">
              Two teams build it. One human layer answers for it.
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {[
              ["Marketing & Creative", "/growth"],
              ["Workflow & Automation", "/workflow"],
            ].map(([name, route], i) => (
              <Reveal key={route} delay={i * 80}>
                <Link
                  to={route}
                  className="group flex items-center justify-between rounded-2xl border border-black/10 bg-[var(--bg-contrast)] px-6 py-5 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--ember)]/40"
                  data-testid={`crosslink-${route.slice(1)}`}
                >
                  <span className="text-[15px] font-semibold tracking-tight">
                    {name}
                  </span>
                  <ArrowRight
                    size={18}
                    className="text-[var(--ember)] transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI Standards line */}
      <section className="bg-[var(--dark)] py-24 text-[var(--dark-text)] md:py-28" data-testid="industry-standards">
        <div className="mx-auto flex max-w-4xl flex-col items-start gap-6 px-6 md:px-12">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full bg-[var(--ember)]/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--ember)]">
              <ShieldCheck size={15} /> AI Standards
            </span>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-2xl font-semibold leading-snug tracking-[-0.02em] sm:text-3xl">
              {industry.standardsLine}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Vertical case study */}
      <section className="bg-[var(--bg-base)] py-28 md:py-36" data-testid="industry-case">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <Reveal><Label>Proof in {industry.name.toLowerCase()}</Label></Reveal>
          <Reveal delay={100}>
            <div className="mt-8 rounded-2xl border border-black/5 bg-[var(--bg-contrast)] p-10">
              <span className="text-5xl font-semibold tracking-[-0.03em] text-[var(--ember)] sm:text-6xl">
                {industry.caseStudy.metric}
              </span>
              <p className="mt-3 text-sm font-semibold uppercase tracking-[0.1em] text-[var(--muted)]">
                {industry.caseStudy.label}
              </p>
              <Quote size={24} className="mt-7 text-[var(--ember)]/40" />
              <p className="mt-3 text-lg leading-relaxed text-[var(--ink)]">
                {industry.caseStudy.quote}
              </p>
              <div className="mt-7 flex items-center gap-3">
                <img
                  src={industry.caseStudy.photo}
                  alt={industry.caseStudy.name}
                  className="h-12 w-12 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-semibold">
                    {industry.caseStudy.name}
                  </p>
                  <p className="text-[13px] text-[var(--muted)]">
                    {industry.caseStudy.title}
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />

      <DemoModal
        open={!!modalAgent}
        agent={modalAgent}
        triggerRef={triggerRef}
        onClose={() => setModalAgent(null)}
      />
    </>
  );
}
